import { generateUniqueIdentifier } from 'commons/utils';
import { WbEnvironments, WbSessionActions } from 'enums';
import { InterWbResponseHandler } from './InterWbResponseHandler';
export class InterWebviewBridge extends InterWbResponseHandler {
    constructor() {
        super();
        this.isClientSide = typeof window !== 'undefined';
        this.isAndroid = this.isClientSide
            ? typeof window?.Android?.execute === 'function'
            : false;
        this.getNativeBridge = () => {
            if (!this.isClientSide) {
                return undefined;
            }
            if (typeof window?.Android?.execute === 'function') {
                return window.Android;
            }
            if (typeof window?.webkit?.messageHandlers?.execute?.postMessage === 'function') {
                return window.webkit.messageHandlers;
            }
            return undefined;
        };
        this.hasNativeBridge = () => !!this.getNativeBridge();
        /**
         * @summary Registra um evento previamente mapeado na bridge
         */
        this.addWebviewEventListener = (type, _callback) => this.registerListener(type, _callback);
        /**
         * @summary Remove eventos
         * @param _callback - função a ser removida
         */
        this.removeWebviewEventListener = (type, _callback) => window?.removeEventListener(type, _callback);
        this.info('Constructor', 'private', `Iniciando classe`);
    }
    /**
     * @summary Retorna uma instância única de InterWebviewBridge
     * @returns - instanceof InterWebviewBridge
     */
    static getInstance() {
        if (!InterWebviewBridge.instance) {
            InterWebviewBridge.instance = new InterWebviewBridge();
            if (InterWebviewBridge.instance.hasNativeBridge()) {
                const url = window?.location?.href ?? '';
                InterWebviewBridge.instance.execute({
                    action: WbSessionActions.REQUEST_ANALYTICS,
                    metadata: { name: 'WEBVIEW_URL', params: { mainUrl: url, native: 'false' } },
                });
            }
        }
        return InterWebviewBridge.instance;
    }
    static clearInstance() {
        if (InterWebviewBridge.instance) {
            this.instance = null;
        }
    }
    registerListener(evtId, _callback) {
        return new Promise((resolve, reject) => {
            if (!this.isClientSide) {
                reject(new Error('Ambiente não detectado!'));
            }
            const listener = (event) => {
                try {
                    const data = this.wbResponseHandler(event);
                    this.info(event.detail.action, evtId, `Recebido: ${JSON.stringify(data)}`);
                    resolve(data);
                }
                catch (error) {
                    this.error(event.detail.action, evtId, String(error));
                    reject(error);
                }
                window?.removeEventListener(evtId, listener);
            };
            window?.addEventListener(evtId, _callback || listener);
        });
    }
    /**
     * Retorna o ambiente atual
     * @returns - string: 'BROWSER', 'ANDROID' or 'IOS'
     */
    getEnvironment() {
        if (!this.isClientSide) {
            return WbEnvironments.SERVER;
        }
        const isMobile = !!this.getNativeBridge();
        if (!isMobile) {
            return WbEnvironments.BROWSER;
        }
        if (this.isAndroid) {
            return WbEnvironments.ANDROID;
        }
        return WbEnvironments.IOS;
    }
    /**
     * Faz uma request para a bridge Webview
     * @param request - IWbBaseRequest com o metadata setado
     * @returns - Promise com o IWbBaseResponse.data esperado
     */
    execute(request) {
        const bridge = this.getNativeBridge();
        if (!bridge) {
            return Promise.reject(new Error('Mobile environment not detected!'));
        }
        const eventName = generateUniqueIdentifier();
        const promise = this.registerListener(eventName);
        const body = JSON.stringify({ ...request, ...{ evtId: eventName } });
        if (this.isAndroid) {
            bridge.execute(body);
        }
        else {
            bridge.execute.postMessage(body);
        }
        this.info(request.action, eventName, `Executando ${body}`);
        return promise;
    }
}
